import {
  forwardRef,
  useId,
  type FieldsetHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { classNames, joinIds } from "./component-model";
import { buildFieldMetadata, type FieldControlProps } from "./form-model";

export interface FormFieldProps {
  readonly id?: string;
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: ReactNode;
  readonly required?: boolean;
  readonly className?: string;
  readonly children: (controlProps: FieldControlProps) => ReactNode;
}

export function FormField({
  id,
  label,
  hint,
  error,
  required = false,
  className,
  children,
}: FormFieldProps) {
  const generatedId = useId();
  const hasHint = hint !== null && hint !== undefined;
  const hasError = error !== null && error !== undefined;
  const metadata = buildFieldMetadata(id ?? `wh-field-${generatedId}`, {
    hasHint,
    hasError,
    required,
  });

  return (
    <div
      className={classNames(
        "wh-field",
        hasError && "wh-field--invalid",
        className,
      )}
    >
      <label className="wh-field__label" htmlFor={metadata.controlId}>
        <span>{label}</span>
        {required && <span className="wh-field__required">Required</span>}
      </label>
      {children(metadata.controlProps)}
      {hasHint && (
        <p className="wh-field__hint" id={metadata.hintId}>
          {hint}
        </p>
      )}
      {hasError && (
        <p className="wh-field__error" id={metadata.errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

export type TextInputProps = InputHTMLAttributes<HTMLInputElement>;

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput({ className, ...props }, ref) {
    return (
      <input
        {...props}
        ref={ref}
        className={classNames("wh-control", "wh-input", className)}
      />
    );
  },
);

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea({ className, ...props }, ref) {
    return (
      <textarea
        {...props}
        ref={ref}
        className={classNames("wh-control", "wh-textarea", className)}
      />
    );
  },
);

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select
        {...props}
        ref={ref}
        className={classNames("wh-control", "wh-select", className)}
      >
        {children}
      </select>
    );
  },
);

export interface ChoiceFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "children" | "type"> {
  readonly type?: "checkbox" | "radio";
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: ReactNode;
}

export const ChoiceField = forwardRef<HTMLInputElement, ChoiceFieldProps>(
  function ChoiceField(
    {
      id,
      type = "checkbox",
      label,
      hint,
      error,
      required = false,
      className,
      ...props
    },
    ref,
  ) {
    const generatedId = useId();
    const hasHint = hint !== null && hint !== undefined;
    const hasError = error !== null && error !== undefined;
    const metadata = buildFieldMetadata(id ?? `wh-choice-${generatedId}`, {
      hasHint,
      hasError,
      required,
    });
    const describedBy = joinIds(
      props["aria-describedby"],
      metadata.controlProps["aria-describedby"],
    );
    const controlProps = {
      ...metadata.controlProps,
      ...(describedBy ? { "aria-describedby": describedBy } : {}),
    };

    return (
      <div
        className={classNames(
          "wh-choice-field",
          hasError && "wh-choice-field--invalid",
        )}
      >
        <label className="wh-choice-field__label" htmlFor={metadata.controlId}>
          <input
            {...props}
            {...controlProps}
            ref={ref}
            type={type}
            className={classNames("wh-choice", className)}
          />
          <span>{label}</span>
          {required && <span className="wh-field__required">Required</span>}
        </label>
        {hasHint && (
          <p className="wh-field__hint" id={metadata.hintId}>
            {hint}
          </p>
        )}
        {hasError && (
          <p className="wh-field__error" id={metadata.errorId}>
            {error}
          </p>
        )}
      </div>
    );
  },
);

export interface FormGroupProps
  extends Omit<FieldsetHTMLAttributes<HTMLFieldSetElement>, "children"> {
  readonly legend: ReactNode;
  readonly hint?: ReactNode;
  readonly children: ReactNode;
}

export function FormGroup({
  legend,
  hint,
  children,
  className,
  ...props
}: FormGroupProps) {
  const hintId = `wh-group-${useId()}-hint`;
  const describedBy = joinIds(
    props["aria-describedby"],
    hint ? hintId : undefined,
  );

  return (
    <fieldset
      {...props}
      className={classNames("wh-form-group", className)}
      aria-describedby={describedBy || undefined}
    >
      <legend className="wh-form-group__legend">{legend}</legend>
      {hint && (
        <p className="wh-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      <div className="wh-form-group__content">{children}</div>
    </fieldset>
  );
}

export function FormActions({
  children,
  align = "end",
  className,
}: {
  readonly children: ReactNode;
  readonly align?: "start" | "end" | "between";
  readonly className?: string;
}) {
  return (
    <div
      className={classNames(
        "wh-form-actions",
        `wh-form-actions--${align}`,
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface FormErrorSummaryItem {
  readonly id: string;
  readonly label: ReactNode;
  readonly fieldId: string;
}

export interface FormErrorSummaryProps {
  readonly title?: ReactNode;
  readonly errors: readonly FormErrorSummaryItem[];
  readonly className?: string;
}

export const FormErrorSummary = forwardRef<HTMLElement, FormErrorSummaryProps>(
  function FormErrorSummary(
    { title = "Please correct the following", errors, className },
    ref,
  ) {
    if (errors.length === 0) return null;

    return (
      <section
        ref={ref}
        className={classNames("wh-form-error-summary", className)}
        role="alert"
        tabIndex={-1}
      >
        <h2>{title}</h2>
        <ul>
          {errors.map((error) => (
            <li key={error.id}>
              <a href={`#${error.fieldId}`}>{error.label}</a>
            </li>
          ))}
        </ul>
      </section>
    );
  },
);
